import React, { useState } from "react";
import { stockService } from "../services/apiService";

const TERMS = [
  { title: "Acceptance of Terms", content: "By accessing or using this cloud-based billing software (\"Software\"), the user (\"User\") agrees to be legally bound by these Terms. Continued use constitutes acceptance of all updates and modifications." },
  { title: "Nature of Service", content: "The Software is provided as a cloud-hosted billing and invoicing platform. The Provider does not act as an accountant, tax advisor, or legal authority. All outputs generated are system-based calculations and must be verified by the User." },
  { title: "Cloud Hosting & Data Storage", content: "User data is stored on remote cloud servers managed by third-party infrastructure providers. The Provider does not guarantee permanent availability or retention of data. The User acknowledges risks inherent in cloud systems, including data loss, breaches, or downtime." },
  { title: "Data Ownership & Responsibility", content: "All data entered into the Software remains the property of the User. The User is solely responsible for: Data accuracy, Data legality, Regular data backup/export. The Provider shall not be liable for incorrect, incomplete, or illegal data." },
  { title: "Data Loss & Backup Disclaimer", content: "The Provider shall not be responsible for any data loss, corruption, or unavailability, including due to: Server crashes, Database failures, Cyber-attacks, Accidental deletion, Third-party service failure. Users are strongly advised to maintain independent offline or external backups." },
  { title: "Service Availability (No Uptime Guarantee)", content: "The Software is provided on a best-effort basis. The Provider does not guarantee: 100% uptime, Continuous access, Error-free performance. Scheduled or emergency maintenance may cause temporary downtime. No compensation or liability shall arise due to service interruptions." },
  { title: "Third-Party Dependencies", content: "The Software depends on third-party services such as: Cloud hosting providers, Payment gateways, APIs and integrations. The Provider shall not be liable for failures, delays, or security issues caused by such third parties." },
  { title: "Security Disclaimer", content: "While reasonable security measures are implemented, the Provider does not guarantee absolute security of data. The User is responsible for: Securing login credentials, Preventing unauthorized access. The Provider is not liable for: Data breaches, Hacking incidents, Unauthorized access due to user negligence." },
  { title: "Limitation of Liability", content: "To the fullest extent permitted by law: The Provider shall not be liable for: Data loss, Business interruption, Financial losses, Loss of goodwill or reputation. Total liability shall be strictly limited to the amount paid for the service (if any)." },
  { title: "Disclaimer of Warranties", content: "The Software is provided \"AS IS\" and \"AS AVAILABLE\" without warranties of any kind, including: Accuracy or reliability, Fitness for a particular purpose, Availability or uptime, Freedom from bugs or errors." },
  { title: "Compliance & Tax Responsibility", content: "The User is solely responsible for compliance with applicable laws including Goods and Services Tax (GST). The Software only assists in calculation and record generation and does not guarantee legal compliance." },
  { title: "Indemnification", content: "The User agrees to indemnify and hold harmless the Provider against any claims, damages, or liabilities arising from: Use or misuse of the Software, Incorrect billing or invoicing, Legal or tax non-compliance, Third-party disputes." },
  { title: "Suspension & Termination", content: "The Provider may suspend or terminate access: For violation of terms, For security concerns, For non-payment (if applicable). Data may be deleted after termination without liability." },
  { title: "Data Retention & Deletion", content: "The Provider reserves the right to delete inactive or terminated account data after a defined period. Users must export required data before account closure." },
  { title: "Force Majeure", content: "The Provider shall not be liable for failure or delay due to events beyond control, including: Natural disasters, Internet outages, Cyber-attacks, Government restrictions." },
  { title: "Modifications", content: "The Provider may update or modify the Software and Terms at any time without prior notice. Continued use constitutes acceptance of updated Terms." },
  { title: "Governing Law & Jurisdiction", content: "These Terms shall be governed by the laws of India. All disputes shall fall under the jurisdiction of Delhi courts." },
  { title: "Entire Agreement", content: "These Terms constitute the complete agreement between the User and the Provider." },
];

const FOOTER_TEXT = "Cloud software provided \"AS IS\". No liability for data loss, downtime, or security breaches. User responsible for backups, data accuracy, and compliance. Subject to Delhi jurisdiction.";

const TC_VERSION = "1.0";

const TermsCondition = ({ onAccepted }) => {
  const [signature, setSignature] = useState("");
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const canSubmit = signature.trim().length >= 3 && checked && scrolledToBottom;

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 30) {
      setScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError("");
    try {
      await stockService.acceptTerms(signature.trim(), TC_VERSION);
      onAccepted();
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-[95vw] md:max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4 flex items-center gap-3 flex-shrink-0">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
            <i className="fas fa-file-contract text-white text-lg" />
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Terms & Conditions</h2>
            <p className="text-blue-100 text-xs">Please read completely and accept to continue</p>
          </div>
        </div>

        {/* Scroll indicator */}
        {!scrolledToBottom && (
          <div className="bg-amber-50 border-b border-amber-200 px-4 py-1.5 flex items-center gap-2 flex-shrink-0">
            <i className="fas fa-arrow-down text-amber-500 text-xs animate-bounce" />
            <span className="text-xs text-amber-700 font-medium">Scroll to bottom to enable acceptance</span>
          </div>
        )}

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4" onScroll={handleScroll}>
          <ol className="space-y-4">
            {TERMS.map((term, idx) => (
              <li key={idx} className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 bg-indigo-100 text-indigo-700 rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                  {idx + 1}
                </span>
                <div>
                  <h4 className="text-sm font-semibold text-gray-800">{term.title}</h4>
                  <p className="text-sm text-gray-600 leading-relaxed mt-0.5">{term.content}</p>
                </div>
              </li>
            ))}
          </ol>

          {/* Legal Footer */}
          <div className="mt-6 p-3 bg-gray-100 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-600 font-medium italic">
              <i className="fas fa-gavel mr-1 text-gray-500" />
              {FOOTER_TEXT}
            </p>
          </div>
        </div>

        {/* Footer - Signature & Accept */}
        <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex-shrink-0">
          {error && (
            <p className="text-red-600 text-xs mb-2 font-medium">
              <i className="fas fa-exclamation-circle mr-1" />{error}
            </p>
          )}

          <div className="mb-3">
            <label className="block text-xs font-semibold text-gray-600 mb-1">
              Digital Signature <span className="text-red-500">*</span>
              <span className="font-normal text-gray-400 ml-1">(Type your full name as signature)</span>
            </label>
            <div className="relative">
              <input
                type="text"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                placeholder="Type your full name here"
                className="w-full px-4 py-3 border-b-2 border-gray-400 rounded-none bg-gray-50 text-lg focus:border-indigo-600 focus:outline-none focus:bg-white transition-all"
                style={{ fontFamily: "'Segoe Script', 'Bradley Hand', cursive", letterSpacing: "1px" }}
              />
              <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-gray-400 to-transparent" />
              {signature.trim().length >= 3 && (
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-green-500">
                  <i className="fas fa-check-circle" />
                </span>
              )}
            </div>
            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
              <i className="fas fa-pen-nib text-[10px]" />
              This serves as your legally binding digital signature
            </p>
          </div>

          <label className="flex items-start gap-2.5 cursor-pointer mb-4">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              disabled={!scrolledToBottom}
              className="w-4 h-4 mt-0.5 accent-indigo-600 cursor-pointer"
            />
            <span className="text-sm text-gray-700">
              I have read and accept the <strong>Terms & Conditions</strong>. I confirm that I am authorized to accept on behalf of my organization.
            </span>
          </label>

          <button
            onClick={handleAccept}
            disabled={!canSubmit || saving}
            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg font-semibold text-sm transition-all ${
              canSubmit && !saving
                ? "bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white shadow-md"
                : "bg-gray-200 text-gray-400 cursor-not-allowed"
            }`}
          >
            {saving ? (
              <><i className="fas fa-spinner fa-spin" /> Saving...</>
            ) : (
              <><i className="fas fa-check-circle" /> Accept & Continue</>
            )}
          </button>

          {!scrolledToBottom && (
            <p className="text-xs text-gray-400 text-center mt-2">
              <i className="fas fa-info-circle mr-1" />
              Please scroll through all terms before accepting
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsCondition;
